import { tiptapToBackend, TiptapConversionResult } from './TipTapAdapter'
import { CreateBlogPayload, ArticleFormValues, UpdateBlogPayload } from 'Models'


/**
 * Form values -> explicit backend payloads. Nothing here submits a whole
 * article response, and nothing auto-fills SEO/excerpt fields: an unset
 * override stays unset so the backend's computed values keep applying.
 */

export interface BuildDraftPayloadResult {
  payload: CreateBlogPayload
  /** TipTap node types that could not be converted. Block the save and tell the admin. */
  unsupportedNodeTypes: string[]
}

export interface BuildUpdatePayloadResult {
  payload: UpdateBlogPayload
  unsupportedNodeTypes: string[]
}

const trimmedOrUndefined = (value?: string): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

const convert = (values: ArticleFormValues | null): TiptapConversionResult =>
  values?.content ? tiptapToBackend(values.content) : { blocks: [], unsupportedNodeTypes: [] }

/**
 * Payload for the first save of a new article (POST). Only `title` is
 * required by the backend; everything else is included only when filled in.
 * No publish-level validation happens here.
 */
export const buildCreateDraftPayload = (
  values: ArticleFormValues,
  options: { author?: string } = {},
): BuildDraftPayloadResult => {
  const { blocks, unsupportedNodeTypes } = convert(values)

  const payload: CreateBlogPayload = {
    title: values.title.trim(),
    publishedStatus: 'draft',
  }
  if (options.author) payload.author = options.author

  const excerpt = trimmedOrUndefined(values.excerpt)
  if (excerpt) payload.excerpt = excerpt
  if (values.category) payload.category = values.category
  if (values.tags?.length) payload.tags = values.tags
  if (blocks.length) payload.content = blocks

  if (values.coverImage) {
    payload.coverImage = values.coverImage
    const alt = trimmedOrUndefined(values.coverImageAltText)
    if (alt) payload.coverImageAltText = alt
  }

  const metaTitle = trimmedOrUndefined(values.metaTitle)
  if (metaTitle) payload.metaTitle = metaTitle
  const metaDescription = trimmedOrUndefined(values.metaDescription)
  if (metaDescription) payload.metaDescription = metaDescription
  const canonicalUrl = trimmedOrUndefined(values.canonicalUrl)
  if (canonicalUrl) payload.canonicalUrl = canonicalUrl
  const socialImageUrl = trimmedOrUndefined(values.socialImageUrl)
  if (socialImageUrl) payload.socialImageUrl = socialImageUrl

  return { payload, unsupportedNodeTypes }
}

/**
 * Diff-based update (PUT). Sends `version` plus only the fields that differ
 * from `previousValues` (the last values confirmed saved). Pass
 * `previousValues: null` to send every field.
 *
 * Cleared optional fields are sent as `null`, never as empty strings.
 * Content is compared as converted backend blocks, not raw TipTap JSON, so
 * harmless editor re-normalisation doesn't count as a change.
 */
export const buildUpdatePayload = (
  values: ArticleFormValues,
  previousValues: ArticleFormValues | null,
  context: { version: number; author?: string; publishedStatus?: 'draft' | 'published' },
): BuildUpdatePayloadResult => {
  const { blocks, unsupportedNodeTypes } = convert(values)
  const previousBlocks = previousValues ? convert(previousValues).blocks : null
  const payload: UpdateBlogPayload = { version: context.version }

  const changed = <K extends keyof ArticleFormValues>(key: K): boolean =>
    previousValues === null || JSON.stringify(values[key]) !== JSON.stringify(previousValues[key])

  if (changed('title')) payload.title = values.title.trim()
  if (changed('excerpt')) payload.excerpt = trimmedOrUndefined(values.excerpt) ?? null
  if (changed('category')) payload.category = values.category || null
  if (changed('tags')) payload.tags = values.tags

  if (previousBlocks === null || JSON.stringify(blocks) !== JSON.stringify(previousBlocks)) {
    payload.content = blocks
  }

  if (changed('coverImage')) payload.coverImage = values.coverImage ?? null
  if (changed('coverImageAltText')) payload.coverImageAltText = values.coverImageAltText.trim()

  if (changed('metaTitle')) payload.metaTitle = trimmedOrUndefined(values.metaTitle) ?? null
  if (changed('metaDescription')) payload.metaDescription = trimmedOrUndefined(values.metaDescription) ?? null
  if (changed('canonicalUrl')) payload.canonicalUrl = trimmedOrUndefined(values.canonicalUrl) ?? null
  if (changed('socialImageUrl')) payload.socialImageUrl = trimmedOrUndefined(values.socialImageUrl) ?? null

  if (context.publishedStatus) payload.publishedStatus = context.publishedStatus
  if (context.author) payload.author = context.author

  return { payload, unsupportedNodeTypes }
}

/** True when the payload carries anything besides `version`. Lets the caller skip a request that would change nothing. */
export const hasPayloadChanges = (payload: UpdateBlogPayload): boolean =>
  Object.entries(payload).some(([key, value]) => key !== 'version' && value !== undefined)

export const buildUnpublishPayload = (version: number): UpdateBlogPayload => ({
  version,
  publishedStatus: 'draft',
})
